import { useQuery } from '@tanstack/react-query';
import { useTraccarMessage } from './useTraccarSocket';
import { geofencesApi } from '@/api/endpoints/geofences';
import { commandsApi, GT06_COMMANDS } from '@/api/endpoints/commands';
import { useFleetStore } from '@/stores/fleet';

/**
 * Escuta eventos de entrada/saída de geocerca via WebSocket e dispara o
 * comando GT06 de bloqueio (`RELAY,1#`) automaticamente quando a geocerca
 * tem `attributes.blockOnEnter` ou `attributes.blockOnExit` ativos.
 *
 * Mantém um Set de combinações (deviceId+geofenceId+kind) já processadas
 * para evitar disparo duplicado se o servidor reentregar o mesmo evento.
 *
 * Mostra notificação visual via Notification API se permitido.
 */
const dispatched = new Set<string>();

function attrBool(attrs: Record<string, unknown> | undefined, key: string): boolean {
  if (!attrs) return false;
  const v = attrs[key];
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.toLowerCase() === 'true';
  return false;
}

function notify(title: string, body: string) {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body, icon: '/fismatek-icon.png', tag: title });
  } catch {
    /* alguns browsers exigem ServiceWorker para Notification em mobile */
  }
}

export function useGeofenceAutoBlock() {
  const geofencesQuery = useQuery({
    queryKey: ['geofences'],
    queryFn: () => geofencesApi.list(),
    staleTime: 60_000,
  });

  useTraccarMessage((msg) => {
    if (!msg.events?.length) return;
    if (!geofencesQuery.data) return;
    const devices = useFleetStore.getState().devices;
    const geofencesById = new Map(geofencesQuery.data.map((g) => [g.id, g]));

    for (const event of msg.events) {
      if (event.type !== 'geofenceEnter' && event.type !== 'geofenceExit') continue;
      if (event.geofenceId == null || event.deviceId == null) continue;
      const geofence = geofencesById.get(event.geofenceId);
      if (!geofence) continue;

      const kind = event.type === 'geofenceEnter' ? 'enter' : 'exit';
      const shouldBlock =
        kind === 'enter'
          ? attrBool(geofence.attributes, 'blockOnEnter')
          : attrBool(geofence.attributes, 'blockOnExit');

      const dedupKey = `${event.id}:${event.deviceId}:${event.geofenceId}:${kind}`;
      if (dispatched.has(dedupKey)) continue;
      dispatched.add(dedupKey);

      const device = devices.get(event.deviceId);
      const deviceLabel = device?.name ?? `Máquina ${event.deviceId}`;
      const geofenceLabel = geofence.name;

      if (shouldBlock) {
        commandsApi
          .send(event.deviceId, GT06_COMMANDS.block)
          .then(() => {
            notify(
              'Bloqueio automático enviado',
              `${deviceLabel} ${kind === 'enter' ? 'entrou' : 'saiu'} de "${geofenceLabel}" e RELAY,1# foi disparado.`,
            );
          })
          .catch((err) => {
            console.warn('[auto-block] falha ao enviar comando', err);
            notify(
              'Bloqueio automático falhou',
              `${deviceLabel} ${kind === 'enter' ? 'entrou' : 'saiu'} de "${geofenceLabel}" mas o comando não foi aceito.`,
            );
          });
      } else {
        // Notifica geocerca normal mesmo sem auto-bloqueio
        notify(
          kind === 'enter' ? 'Entrou na geocerca' : 'Saiu da geocerca',
          `${deviceLabel} · ${geofenceLabel}`,
        );
      }
    }
  });
}
